import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SIGNED_OUT_EVENT } from './signedOutEvent';

interface AccessibleProject {
  projectId: number;
  projectName: string;
}

interface ProjectConfig {
  project_id: number;
  project_name: string | null;
  display_title: string | null;
  theme_primary_color: string;
  theme_secondary_color: string;
  logo_url: string | null;
  favicon_url: string | null;
  description: string | null;
  is_active: boolean;
}

interface User {
  id: string;
  email: string;
  userId?: number;
  userCode?: number;
  userName?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  accessibleProjects?: AccessibleProject[];
  adminRole?: 'super_admin' | 'admin' | null;
  adminProjectId?: number | null;
  adminProjectIds?: number[];
  isExternalUser?: boolean;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isMockAuth: boolean;
  externalToken: string | null;
  permissions: string[];
  sessionExpired: boolean;
  selectedProjectId: number | null;
  projectConfig: ProjectConfig | null;
  projectListVersion: number;
  setAuth: (token: string, user: User, options?: { isMock?: boolean }) => void;
  setExternalToken: (token: string) => void;
  setPermissions: (permissions: string[]) => void;
  setSessionExpired: (expired: boolean) => void;
  setSelectedProjectId: (projectId: number) => void;
  setProjectConfig: (config: ProjectConfig | null) => void;
  refreshProjectList: () => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isMockAuth: false,
      externalToken: null,
      permissions: [],
      sessionExpired: false,
      selectedProjectId: null,
      projectConfig: null,
      projectListVersion: 0,
      setAuth: (token, user, options) =>
        set(() => ({
          token,
          user,
          isMockAuth: Boolean(options?.isMock),
          externalToken: token,
          sessionExpired: false,
          selectedProjectId: user.adminProjectId || user.accessibleProjects?.[0]?.projectId || null,
        })),
      setExternalToken: (token) => set({ externalToken: token }),
      setPermissions: (permissions) => set({ permissions }),
      setSessionExpired: (expired) => set({ sessionExpired: expired }),
      setSelectedProjectId: (projectId) => set({ selectedProjectId: projectId }),
      setProjectConfig: (config) => set({ projectConfig: config }),
      refreshProjectList: () => set((state) => ({ projectListVersion: state.projectListVersion + 1 })),
      clearAuth: () => {
        set({ token: null, user: null, isMockAuth: false, externalToken: null, permissions: [], sessionExpired: false, selectedProjectId: null, projectConfig: null, projectListVersion: 0 });
        // services/api.ts hears this and asks the server to drop the sign-in pass
        // cookie, which page scripts cannot reach themselves.
        if (typeof window !== 'undefined') window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
